import {
  GlobalPermission,
  SystemPermission,
  User,
} from '@wavingroup/aqora-v2-api/wavin/aqora/v2/user_pb';
import { TFunction } from 'i18next';
import { RoleBySystem } from '~/pages/Users/types';
import { RolesModel, UserRole } from '~/shared/models/system/RolesModel';
import { assertUnreachable } from '~/types/assert-type';

export class UserModel {
  readonly name: string;

  readonly id: string;

  readonly email: string;

  readonly permissions: SystemPermission[];

  readonly globalPermissions: GlobalPermission[];

  readonly systemsByRole: Map<UserRole, string[]>;

  readonly roleBySystem: Map<string, UserRole>;

  constructor(user: User) {
    this.name = user.name;
    this.id = user.name.replace('users/', '');
    this.email = user.email;
    this.permissions = user.systemPermissions;
    this.systemsByRole = this.permissionsToSystemsByRole();
    this.roleBySystem = this.permissionsToRoleBySystem();
    this.globalPermissions = user.globalPermissions;
  }

  private permissionsToRoleBySystem() {
    const roleBySystem = new Map<string, UserRole>();

    this.permissions.forEach(({ systemName, permissions }) => {
      const role = RolesModel.permissionsToRole(permissions);
      roleBySystem.set(systemName, role);
    });

    return roleBySystem;
  }

  private permissionsToSystemsByRole() {
    const systemsByRole = new Map<UserRole, string[]>();

    this.permissions.forEach(({ systemName, permissions }) => {
      const role = RolesModel.permissionsToRole(permissions);

      const systems = systemsByRole.get(role) || [];
      systems.push(systemName);
      systemsByRole.set(role, systems);
    });

    return systemsByRole;
  }

  static newRolesToPermissions(newRoles: RoleBySystem) {
    const newPermissions: SystemPermission[] = [];

    newRoles.forEach((role, systemName) => {
      newPermissions.push(
        new SystemPermission({
          systemName,
          permissions: RolesModel.roleToPermissions(role),
        }),
      );
    });

    return newPermissions;
  }

  getEmptyPermissionsForAllUserSystems() {
    return (
      this.permissions?.map(
        ({ systemName }) =>
          new SystemPermission({
            systemName,
            permissions: [],
          }),
      ) || []
    );
  }

  static getGlobalPermissionLabel(
    globalPermission: GlobalPermission,
    t: TFunction,
  ) {
    switch (globalPermission) {
      case GlobalPermission.ADVICE:
        return t('userManagement.globalPermissions.advice');
      case GlobalPermission.CREATE_SYSTEM:
        return t('userManagement.globalPermissions.createSystem');
      case GlobalPermission.STATISTICS:
        return t('userManagement.globalPermissions.statistics');
      case GlobalPermission.USER_ADMIN:
        return t('userManagement.globalPermissions.userAdmin');
      case GlobalPermission.WAVIN_ONLY:
        return t('userManagement.globalPermissions.wavinOnly');
      case GlobalPermission.WAVIN_ONLY_ADMIN:
        return t('userManagement.globalPermissions.wavinOnlyAdmin');
      case GlobalPermission.UNSPECIFIED:
        throw new Error('Unspecified global permission');
      default:
        return assertUnreachable(globalPermission);
    }
  }

  getNewGlobalPermissions(
    newGlobalPermissions: GlobalPermission[],
    isUserInternalAdmin: boolean,
  ) {
    const newPermissions = [...newGlobalPermissions];

    if (!isUserInternalAdmin) {
      const existingInternalPermissions = this.globalPermissions.filter(
        (permission) =>
          [
            GlobalPermission.WAVIN_ONLY,
            GlobalPermission.WAVIN_ONLY_ADMIN,
          ].includes(permission),
      );
      newPermissions.push(...existingInternalPermissions);
    }

    return newPermissions;
  }

  getGlobalPermissionsToShow(isUserInternalAdmin: boolean) {
    const globalPermissions = this.globalPermissions.filter(
      (permission) =>
        isUserInternalAdmin ||
        ![
          GlobalPermission.WAVIN_ONLY,
          GlobalPermission.WAVIN_ONLY_ADMIN,
        ].includes(permission),
    );

    return globalPermissions;
  }
}
